import { useState, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PillToggle } from "@/components/admin/PillToggle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, X, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { MediaPortfolioManager } from "@/components/admin/MediaPortfolioManager";
import { TagSelector } from "@/components/admin/TagSelector";
import { mapDatabaseError } from "@/lib/errorUtils";

type Talent = {
  id: string;
  name: string;
  slug: string;
  specialty: string;
  bio: string | null;
  image_url: string | null;
  languages: string[];
  is_featured: boolean;
  is_active: boolean;
  display_order: number;
};

interface TalentEditDialogProps {
  talent: Talent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const emptyTalent = {
  name: "",
  slug: "",
  specialty: "",
  bio: "",
  image_url: "",
  languages: [] as string[],
  is_featured: false,
  is_active: true,
  display_order: 0,
};

export const TalentEditDialog = ({ talent, open, onOpenChange, onSuccess }: TalentEditDialogProps) => {
  const queryClient = useQueryClient();
  
  // Fetch specialties from the master specialties table
  const { data: existingSpecialties = [] } = useQuery({
    queryKey: ["talent-specialties"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("specialties")
        .select("name")
        .order("name");
      
      if (error) throw error;
      return data?.map((s) => s.name) || [];
    },
  });

  // Fetch existing languages for tag suggestions
  const { data: existingLanguages = [] } = useQuery({
    queryKey: ["talent-languages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("languages")
        .not("languages", "is", null);
      
      if (error) throw error;
      
      // Flatten and get unique languages
      const allLanguages = data?.flatMap((t) => t.languages || []) || [];
      const uniqueLanguages = [...new Set(allLanguages)].sort();
      return uniqueLanguages as string[];
    },
  });

  const [formData, setFormData] = useState(
    talent
      ? {
          name: talent.name,
          slug: talent.slug,
          specialty: talent.specialty,
          bio: talent.bio || "",
          image_url: talent.image_url || "",
          languages: talent.languages || [],
          is_featured: talent.is_featured,
          is_active: talent.is_active,
          display_order: talent.display_order,
        }
      : emptyTalent
  );
  const [specialtyTags, setSpecialtyTags] = useState<string[]>(
    talent?.specialty ? talent.specialty.split(",").map(s => s.trim()).filter(Boolean) : []
  );
  const [languageTags, setLanguageTags] = useState<string[]>(
    talent?.languages || []
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when talent changes or dialog opens
  useState(() => {
    if (talent) {
      setFormData({
        name: talent.name,
        slug: talent.slug,
        specialty: talent.specialty,
        bio: talent.bio || "",
        image_url: talent.image_url || "",
        languages: talent.languages || [],
        is_featured: talent.is_featured,
        is_active: talent.is_active,
        display_order: talent.display_order,
      });
      setLanguageTags(talent.languages || []);
      setSpecialtyTags(talent.specialty ? talent.specialty.split(",").map(s => s.trim()).filter(Boolean) : []);
    }
  });

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `talent/${formData.slug || Date.now()}-${Date.now()}.${fileExt}`;

      if (formData.image_url?.includes("media")) {
        const oldPath = formData.image_url.split("/media/")[1];
        if (oldPath) {
          await supabase.storage.from("media").remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from("media")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("media").getPublicUrl(fileName);

      setFormData({ ...formData, image_url: publicUrl });
      toast.success("Image uploaded");
    } catch (error: unknown) {
      toast.error(mapDatabaseError(error));
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!formData.image_url) return;

    try {
      if (formData.image_url.includes("media")) {
        const path = formData.image_url.split("/media/")[1];
        if (path) {
          await supabase.storage.from("media").remove([path]);
        }
      }
      setFormData({ ...formData, image_url: "" });
      toast.success("Image removed");
    } catch (error: unknown) {
      toast.error(mapDatabaseError(error));
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData & { id?: string }) => {
      const payload = {
        ...data,
        languages: data.languages,
      };

      if (data.id) {
        const { error } = await supabase
          .from("talent")
          .update(payload)
          .eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("talent").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      queryClient.invalidateQueries({ queryKey: ["public-talent"] });
      onOpenChange(false);
      toast.success("Talent updated");
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Combine specialty tags into comma-separated string
    const specialty = specialtyTags.join(", ");

    saveMutation.mutate({
      ...formData,
      specialty,
      languages: languageTags,
      id: talent?.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Talent</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: formData.slug || generateSlug(e.target.value),
                  });
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-slug">Slug *</Label>
              <Input
                id="edit-slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Specialty *</Label>
            <TagSelector
              value={specialtyTags}
              onChange={setSpecialtyTags}
              availableTags={existingSpecialties}
              placeholder="Select specialties..."
              allowCustom={false}
            />
            {specialtyTags.length === 0 && (
              <p className="text-xs text-muted-foreground">At least one specialty is required</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-bio">Bio</Label>
            <Textarea
              id="edit-bio"
              value={formData.bio}
              onChange={(e) =>
                setFormData({ ...formData, bio: e.target.value })
              }
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label>Languages</Label>
            <TagSelector
              value={languageTags}
              onChange={setLanguageTags}
              availableTags={existingLanguages}
              placeholder="Select or add languages..."
              allowCustom={true}
            />
          </div>

          <div className="space-y-2">
            <Label className="block mb-2">Profile Image</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file);
              }}
            />
            {formData.image_url ? (
              <div className="relative w-32 h-32">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6"
                  onClick={handleImageDelete}
                >
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Upload size={16} className="mr-2" />
                  {isUploading ? "Uploading..." : "Upload"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsMediaPickerOpen(true)}
                >
                  <FolderOpen size={16} className="mr-2" />
                  Browse Library
                </Button>
              </div>
            )}
            {formData.image_url && (
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  Replace
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMediaPickerOpen(true)}
                >
                  Browse Library
                </Button>
              </div>
            )}
            <MediaPicker
              open={isMediaPickerOpen}
              onOpenChange={setIsMediaPickerOpen}
              onSelect={(url) => setFormData({ ...formData, image_url: url })}
              bucket="media"
              folder="talent"
            />
          </div>

          {/* Media Portfolio */}
          {talent && (
            <div className="border-t pt-4">
              <MediaPortfolioManager talentId={talent.id} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Visible</Label>
              <PillToggle
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
                labelOn="Yes"
                labelOff="No"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Featured</Label>
              <PillToggle
                checked={formData.is_featured}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_featured: checked })
                }
                labelOn="Yes"
                labelOff="No"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="edit-display_order">Order</Label>
              <Input
                id="edit-display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    display_order: parseInt(e.target.value) || 0,
                  })
                }
                className="w-20"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
